'use client';

import { FC, useCallback, useEffect, useId, useRef, useState } from 'react';
import { asText } from '@prismicio/helpers';
import { isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';

import { CtaButton } from '@/components';
import { PrismicImageWithAlt } from '@/components/PrismicImageWithAlt';
import { Modal } from '@/components/molecules/modal';
import { useFixedBodyOnPopup } from '@/hooks';
import type { PopUpSlice } from '@/types';

import styles from './PopUp.module.scss';

const TRIGGER = {
  TIME_DELAY: 'Time Delay',
  SCROLL: 'Scroll Percentage',
  PAGE_LOAD: 'Page Load',
  EXIT_INTENT: 'Exit Intent',
} as const;

const FREQUENCY = {
  SESSION: 'One Per Session',
  USER: 'One Per User',
  EVERY: 'Every Visit',
} as const;

const buildStorageKey = (sliceId: string) => `prismic_popup_${sliceId}`;

function safeGetStorageItem(storage: Storage | undefined, key: string) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSetStorageItem(
  storage: Storage | undefined,
  key: string,
  value: string,
) {
  try {
    storage?.setItem(key, value);
  } catch {
    // ignore (storage can be blocked in privacy modes)
  }
}

function hasRenderableContent(slice: PopUpSlice): boolean {
  const p = slice.primary;
  return (
    isFilled.richText(p.title) ||
    isFilled.richText(p.content) ||
    isFilled.richText(p.code_embed) ||
    isFilled.image(p.image) ||
    !!p.cta_button_text?.trim()
  );
}

type PopUpComponentProps = {
  slice: PopUpSlice;
};

const PopUp: FC<PopUpComponentProps> = ({ slice }) => {
  const titleId = useId();
  const descriptionId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [frequencyReady, setFrequencyReady] = useState(false);
  const [isFrequencyBlocked, setIsFrequencyBlocked] = useState(false);
  const hasTriggeredRef = useRef(false);

  const shouldRender = slice.primary.active && hasRenderableContent(slice);
  useFixedBodyOnPopup({ isPopupOpen: isOpen, onlyOnMobile: true });

  const primary = slice.primary;
  const showTitle = isFilled.richText(primary.title);
  const showContent = isFilled.richText(primary.content);
  const showCode = isFilled.richText(primary.code_embed);
  const showImage = isFilled.image(primary.image);
  const ctaLabel = primary.cta_button_text?.trim() ?? '';

  const timeDelayMs =
    typeof primary.time_delay === 'number' ? primary.time_delay : 3000;

  const scrollPercentRaw =
    typeof primary.scroll_percentage === 'number'
      ? primary.scroll_percentage
      : 50;
  const scrollPercent = Math.min(100, Math.max(0, scrollPercentRaw));

  const openPopup = useCallback(() => {
    if (hasTriggeredRef.current) {
      return;
    }
    hasTriggeredRef.current = true;
    setIsOpen(true);
  }, []);

  const closePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    const freq = primary.display_frequency;
    const key = buildStorageKey(slice.id);
    if (
      freq === FREQUENCY.SESSION &&
      safeGetStorageItem(sessionStorage, key) === '1'
    ) {
      setIsFrequencyBlocked(true);
    } else if (
      freq === FREQUENCY.USER &&
      safeGetStorageItem(localStorage, key) === '1'
    ) {
      setIsFrequencyBlocked(true);
    }
    setFrequencyReady(true);
  }, [primary.display_frequency, slice.id]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const freq = primary.display_frequency;
    const key = buildStorageKey(slice.id);
    if (freq === FREQUENCY.SESSION) {
      safeSetStorageItem(sessionStorage, key, '1');
    } else if (freq === FREQUENCY.USER) {
      safeSetStorageItem(localStorage, key, '1');
    }
  }, [isOpen, primary.display_frequency, slice.id]);

  useEffect(() => {
    if (!shouldRender || !frequencyReady || isFrequencyBlocked) {
      return;
    }

    const trigger = primary.trigger_type;
    let cleanup: (() => void) | undefined;

    const runScrollCheck = (): boolean => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const pct = scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100;
      if (pct >= scrollPercent) {
        openPopup();
        return true;
      }
      return false;
    };

    if (trigger === TRIGGER.PAGE_LOAD) {
      openPopup();
    } else if (trigger === TRIGGER.TIME_DELAY) {
      const id = window.setTimeout(openPopup, Math.max(0, timeDelayMs));
      cleanup = () => clearTimeout(id);
    } else if (trigger === TRIGGER.SCROLL) {
      if (runScrollCheck()) {
        return undefined;
      }
      const onScroll = () => {
        if (runScrollCheck()) {
          window.removeEventListener('scroll', onScroll);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanup = () => window.removeEventListener('scroll', onScroll);
    } else if (trigger === TRIGGER.EXIT_INTENT) {
      const onLeave = (e: Event) => {
        if (e instanceof globalThis.MouseEvent && e.clientY <= 0) {
          openPopup();
          document.documentElement.removeEventListener('mouseleave', onLeave);
        }
      };
      document.documentElement.addEventListener('mouseleave', onLeave);
      cleanup = () =>
        document.documentElement.removeEventListener('mouseleave', onLeave);
    }

    return cleanup;
  }, [
    shouldRender,
    frequencyReady,
    isFrequencyBlocked,
    primary.trigger_type,
    openPopup,
  ]);

  const modalAriaLabelledBy = showTitle ? titleId : undefined;
  const modalAriaLabel = !showTitle ? 'Dialog' : undefined;
  const modalAriaDescribedBy = showContent ? descriptionId : undefined;

  if (!shouldRender) {
    return null;
  }

  return (
    <Modal
      ariaDescribedBy={modalAriaDescribedBy}
      ariaLabel={modalAriaLabel}
      ariaLabelledBy={modalAriaLabelledBy}
      className={styles.popUpModal}
      isOpen={isOpen}
      toggle={closePopup}
    >
      <div className={styles.root}>
        {showImage && (
          <div className={styles.imageWrap}>
            <PrismicImageWithAlt
              className={styles.heroImage}
              defaultAlt=""
              field={primary.image}
              fill
              sizes="(max-width: 640px) 100vw, 800px"
              style={{
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        <div className={styles.body}>
          {showTitle && (
            <div className={styles.title} id={titleId}>
              <PrismicRichText field={primary.title} />
            </div>
          )}

          {showContent && (
            <div className={styles.content} id={descriptionId}>
              <PrismicRichText field={primary.content} />
            </div>
          )}

          {showCode && (
            <div
              className={styles.codeBlock}
              dangerouslySetInnerHTML={{
                __html: asText(primary.code_embed),
              }}
            />
          )}

          {ctaLabel.length > 0 && (
            <CtaButton
              className={styles.cta}
              externalLink={primary.cta_button_link}
              externalNewTab={false}
              fitContent
              label={ctaLabel}
              onClick={closePopup}
              variant="primary"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PopUp;
